<template>
  <div>
    <header
      class="pointer-events-none fixed inset-x-0 top-0 z-50 hidden pt-4 md:block"
      dusk="main-header"
      :aria-label="__('Panel navigation')"
    >
      <div class="mx-auto w-full max-w-7xl px-4 md:px-8 lg:px-10">
        <div
          class="liquid-glass liquid-glass-nav pointer-events-auto relative flex h-14 w-full items-center gap-2 rounded-full px-3 transition-[border-color,background-color,box-shadow,backdrop-filter] duration-200 lg:gap-3 lg:px-4"
          :class="{ 'liquid-glass-nav--at-top': !isScrolled }"
        >
          <Link
            :href="$url('/')"
            class="liquid-glass-content relative z-10 flex shrink-0 items-center gap-2.5 ps-1"
            :aria-label="appName"
          >
            <BrandLogo
              class="h-8 w-auto max-w-[3rem] shrink-0"
              img-class="block h-full w-full object-contain brand-logo__image"
            />
            <span
              class="hidden text-sm font-bold tracking-tight text-zinc-900 dark:text-white sm:inline"
            >
              {{ appName }}
            </span>
          </Link>

          <nav
            v-if="!searchExpanded && primaryNavItems.length > 0"
            class="liquid-glass-content hidden min-w-0 flex-1 justify-center md:flex"
            :aria-label="__('Navigation')"
          >
            <div class="inline-flex max-w-full items-center gap-0.5 rounded-full p-0.5">
              <template v-for="item in primaryNavItems" :key="item.key">
                <TopNavMenuDropdown
                  v-if="item.type === 'menu'"
                  :item="item"
                  :model-value="openMenuKey === item.key"
                  @update:model-value="value => setOpenMenu(item.key, value)"
                />
                <TopNavLink
                  v-else
                  :item="item"
                />
              </template>

              <PanelMegaMenu
                v-if="menuNavItems.length > 0"
                v-model="isMegaMenuOpen"
                :items="menuNavItems"
              />
            </div>
          </nav>

          <Transition
            enter-active-class="panel-header-search-enter-active"
            enter-from-class="panel-header-search-enter-from"
            enter-to-class="panel-header-search-enter-to"
            leave-active-class="panel-header-search-leave-active"
            leave-from-class="panel-header-search-leave-from"
            leave-to-class="panel-header-search-leave-to"
          >
            <GlobalSearch
              v-if="globalSearchEnabled && searchExpanded"
              ref="globalSearchRef"
              show-close-button
              compact
              class="liquid-glass-content panel-header-search"
              dusk="global-search-component"
              @close="closeHeaderSearch"
            />
          </Transition>

          <div
            class="liquid-glass-content relative z-10 ms-auto flex shrink-0 items-center gap-1.5 pe-0.5"
          >
            <template v-if="!searchExpanded">
              <button
                v-if="globalSearchEnabled"
                type="button"
                class="flex size-9 items-center justify-center rounded-full text-zinc-500 transition active:scale-95 hover:bg-white/60 dark:text-zinc-300 dark:hover:bg-white/10"
                :aria-label="__('Search')"
                @click.prevent="openHeaderSearch"
              >
                <Icon type="search" width="16" />
              </button>

              <ThemeDropdown compact />

              <div v-if="notificationCenterEnabled" class="relative z-50">
                <NotificationCenter />
              </div>

              <div class="hidden items-center gap-2 sm:flex">
                <UserMenu icon-only />
              </div>

              <button
                v-if="supportsAuthentication"
                type="button"
                class="flex size-9 items-center justify-center rounded-full text-zinc-500 transition active:scale-95 hover:bg-white/60 dark:text-zinc-300 dark:hover:bg-white/10"
                :aria-label="__('Logout')"
                @click.prevent="attemptLogout"
              >
                <svg
                  class="size-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </template>
          </div>
        </div>
      </div>
    </header>

    <header
      class="mobile-fixed-chrome pointer-events-none fixed inset-x-0 top-0 z-50 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] md:hidden"
      :aria-label="__('Panel navigation')"
    >
      <div
        class="liquid-glass liquid-glass-nav pointer-events-auto flex h-[3.25rem] w-full items-center justify-between gap-2 rounded-full px-3 transition-[border-color,background-color,box-shadow,backdrop-filter] duration-200"
        :class="{ 'liquid-glass-nav--at-top': !isScrolled }"
      >
        <Link
          :href="$url('/')"
          class="liquid-glass-content flex min-w-0 items-center gap-2"
          :aria-label="appName"
        >
          <BrandLogo
            class="size-8 shrink-0"
            img-class="block h-full w-full object-contain brand-logo__image"
          />
          <span class="truncate text-sm font-bold text-zinc-900 dark:text-white">
            {{ appName }}
          </span>
        </Link>

        <div class="liquid-glass-content flex items-center gap-1">
          <button
            type="button"
            class="flex size-9 items-center justify-center rounded-full text-zinc-500 transition active:scale-95 hover:bg-white/60 dark:text-zinc-300 dark:hover:bg-white/10"
            :aria-label="__('Open navigation')"
            :aria-expanded="mainMenuShown ? 'true' : 'false'"
            @click.prevent="toggleMainMenu"
          >
            <svg
              class="size-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-75"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-from-class="opacity-75"
        leave-to-class="opacity-0"
      >
        <div
          v-if="mainMenuShown"
          class="fixed inset-0 z-[100] bg-gray-900/50 dark:bg-gray-950/60"
          aria-hidden="true"
          @click.prevent="closeMainMenu"
        />
      </transition>

      <transition
        enter-active-class="panel-mobile-drawer-enter-active"
        enter-from-class="panel-mobile-drawer-enter-from"
        enter-to-class="panel-mobile-drawer-enter-to"
        leave-active-class="panel-mobile-drawer-leave-active"
        leave-from-class="panel-mobile-drawer-leave-from"
        leave-to-class="panel-mobile-drawer-leave-to"
      >
        <div
          v-if="mainMenuShown"
          ref="modalContent"
          class="panel-glass panel-mobile-drawer fixed inset-y-0 start-0 z-[110] flex w-full flex-col"
          @keydown.escape.prevent="closeMainMenu"
        >
          <div class="panel-glass-content flex items-center justify-between gap-2 px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <Link
              :href="$url('/')"
              class="flex min-w-0 flex-1 items-center gap-2 rounded-lg py-1 text-gray-900 hover:text-gray-500 focus:outline-none focus:ring focus:ring-inset active:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:active:text-gray-500"
              :aria-label="appName"
            >
              <BrandLogo
                class="size-9 shrink-0"
                img-class="block h-full w-full object-contain brand-logo__image"
              />
              <span class="truncate text-sm font-bold text-zinc-900 dark:text-white">
                {{ appName }}
              </span>
            </Link>

            <button
              type="button"
              class="flex size-10 shrink-0 items-center justify-center rounded-full text-zinc-500 transition active:scale-95 hover:bg-white/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:text-zinc-300 dark:hover:bg-white/10"
              :aria-label="__('Close Sidebar')"
              @click.prevent="closeMainMenu"
            >
              <svg
                class="size-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div
            class="panel-glass-content flex min-h-0 flex-1 flex-col overflow-hidden px-3"
          >
            <MainMenu />
          </div>

          <div
            class="panel-glass-content panel-mobile-drawer__footer shrink-0 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1.5"
          >
            <MobileUserMenu />
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { Inertia } from '@inertiajs/inertia'
import { useStore } from 'vuex'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useLocalization } from '../composables/useLocalization'
import { useTopNavItems } from '../composables/useTopNavItems'
import TopNavLink from '@/components/Menu/TopNavLink.vue'
import TopNavMenuDropdown from '@/components/Menu/TopNavMenuDropdown.vue'
import PanelMegaMenu from '@/components/Menu/PanelMegaMenu.vue'

const { __ } = useLocalization()

/**
 * Maximum number of sections shown directly in the desktop top bar
 */
const topNavPrimaryLimit = Nova.config('topNavPrimaryLimit') ?? 5

const { primaryNavItems, menuNavItems } = useTopNavItems(topNavPrimaryLimit)

const store = useStore()
const modalContent = ref(null)
const globalSearchRef = ref(null)
const searchExpanded = ref(false)
const isMegaMenuOpen = ref(false)
const openMenuKey = ref(null)
const isScrolled = ref(false)

const SCROLL_BORDER_OFFSET = 8

const { activate, deactivate } = useFocusTrap(modalContent, {
  initialFocus: false,
  fallbackFocus: () => modalContent.value,
  allowOutsideClick: true,
  escapeDeactivates: true,
})

const toggleMainMenu = () => store.commit('toggleMainMenu')

const closeMainMenu = () => store.commit('closeMainMenu')

const openHeaderSearch = async () => {
  openMenuKey.value = null
  isMegaMenuOpen.value = false
  searchExpanded.value = true

  await nextTick()
  globalSearchRef.value?.focusSearch()
}

function setOpenMenu(key, open) {
  if (open) {
    openMenuKey.value = key
    isMegaMenuOpen.value = false
    return
  }

  if (openMenuKey.value === key) {
    openMenuKey.value = null
  }
}

const closeHeaderSearch = () => {
  searchExpanded.value = false
}

const globalSearchEnabled = computed(() => Nova.config('globalSearchEnabled'))

const notificationCenterEnabled = computed(() =>
  Nova.config('notificationCenterEnabled')
)

const supportsAuthentication = computed(
  () =>
    Nova.config('withAuthentication') === true ||
    Nova.config('customLogoutPath') !== false
)

const mainMenuShown = computed(() => store.getters.mainMenuShown)
const appName = computed(() => Nova.config('appName'))

let removeInertiaListener

function updateScrollState() {
  const scrollTop =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0

  isScrolled.value = scrollTop > SCROLL_BORDER_OFFSET
}

async function attemptLogout() {
  Nova.$emit('confirm-logout')
}

watch(
  () => isMegaMenuOpen.value,
  open => {
    if (open) {
      openMenuKey.value = null
    }
  }
)

watch(
  () => mainMenuShown.value,
  async isOpen => {
    if (!isOpen) {
      document.body.classList.remove('overflow-y-hidden')
      Nova.resumeShortcuts()
      deactivate()
      return
    }

    document.body.classList.add('overflow-y-hidden')
    Nova.pauseShortcuts()

    await nextTick()

    if (!modalContent.value) {
      return
    }

    activate()
  }
)

onMounted(() => {
  updateScrollState()
  window.addEventListener('scroll', updateScrollState, { passive: true })
  removeInertiaListener = Inertia.on('success', () => {
    nextTick(() => updateScrollState())
  })
})

onBeforeUnmount(() => {
  document.body.classList.remove('overflow-y-hidden')
  Nova.resumeShortcuts()
  deactivate()
  window.removeEventListener('scroll', updateScrollState)
  removeInertiaListener?.()
})
</script>
